
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EventObject } from "@/components/calendar/types";
import { Database } from "@/integrations/supabase/schema";

// Extend the ActionRow type to include the metadata field that exists in the database
// but isn't included in the generated types
type ActionRow = Database['public']['Tables']['actions']['Row'] & {
	metadata?: Record<string, any> | string | null;
};

export const useCalendarEvents = () => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["actions", "Calendar"],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('actions')
				.select('*, metadata')
				.eq('action_type', 'Calendar');
				
			if (error) {
				console.error("Error fetching calendar events:", error);
				throw error;
			}
			
			// Safely cast to our expected type after validating the data
			const validData: ActionRow[] = [];
			
			if (Array.isArray(data)) {
				for (const item of data) {
					if (item && 
						typeof item === 'object' && 
						'id' in item && 
						'name' in item && 
						'action_type' in item) {
						validData.push(item as ActionRow);
					}
				}
			}
			
			return validData;
		},
	});
	
	// Transform into the format expected by the calendar
	const events: EventObject[] = (data || []).map((event: ActionRow) => {
		try {
			// Make sure event exists before accessing properties
			if (!event) {
				throw new Error("Event data is undefined");
			}
			
			// Parse metadata safely
			let metadataObj: Record<string, any> = {};
			if (event.metadata) {
				if (typeof event.metadata === 'string') {
					try {
						metadataObj = JSON.parse(event.metadata as string);
					} catch (e) {
						console.error("Error parsing metadata string:", e);
						metadataObj = {};
					}
				} else if (typeof event.metadata === 'object') {
					metadataObj = event.metadata as Record<string, any>;
				}
			}
			
			return {
				id: event.id,
				title: event.name,
				description: event.description || "",
				start: new Date(event.d_begin || new Date()),
				end: new Date(event.d_end || new Date()),
				color: metadataObj?.color || "#9b87f5", // Default color
				metadata: {
					candidates: Array.isArray(metadataObj?.candidates) ? metadataObj.candidates : [],
					offers: Array.isArray(metadataObj?.offers) ? metadataObj.offers : [],
					companies: Array.isArray(metadataObj?.companies) ? metadataObj.companies : [],
					matches: Array.isArray(metadataObj?.matches) ? metadataObj.matches : [],
					contacts: Array.isArray(metadataObj?.contacts) ? metadataObj.contacts : []
				}
			};
		} catch (error) {
			console.error("Error processing calendar event:", error, event);
			// Return a minimal valid event object
			return {
				id: event?.id || "error-event",
				title: event?.name || "Error loading event",
				description: "Error processing event data",
				start: new Date(),
				end: new Date(),
				color: "#ff0000",
				metadata: {
					candidates: [],
					offers: [],
					companies: [],
					matches: [],
					contacts: []
				}
			};
		}
	});

	return {
		events,
		isLoading,
		error,
		refetch
	};
};
